import { useCallback, useEffect, useRef, useState } from 'react'
import type { MapRef } from 'react-map-gl/mapbox'
import {
  DEFAULT_VIEW_STATE,
  DEGREES_PER_SECOND,
  ROTATION_DURATION_MS,
  LINEAR_EASING,
} from './chatMapConstants'

interface UseMapGlobeRotationArgs {
  mapRef: React.RefObject<MapRef | null>
  isMapLoaded: boolean
  shouldSpin: boolean
  shouldSkipAnimation: boolean
  chatKey?: string
  aiCameraActive?: boolean
}

interface UseMapGlobeRotationResult {
  stopRotation: () => void
}

export function useMapGlobeRotation({
  mapRef,
  isMapLoaded,
  shouldSpin,
  shouldSkipAnimation,
  chatKey,
  aiCameraActive = false,
}: UseMapGlobeRotationArgs): UseMapGlobeRotationResult {
  const hasStoppedRotation = useRef(false)
  const [rotationKey, setRotationKey] = useState(0)
  const prevChatKeyRef = useRef(chatKey)

  const stopRotation = useCallback(() => {
    if (!hasStoppedRotation.current) {
      hasStoppedRotation.current = true
      mapRef.current?.getMap()?.stop()
    }
  }, [mapRef])

  const spinGlobe = useCallback(() => {
    if (hasStoppedRotation.current) return
    const map = mapRef.current?.getMap()
    if (!map) return
    const center = map.getCenter()
    const nextLng = ((center.lng + DEGREES_PER_SECOND + 540) % 360) - 180
    map.easeTo({
      center: [nextLng, center.lat],
      duration: ROTATION_DURATION_MS,
      easing: LINEAR_EASING,
    })
  }, [mapRef])

  // Reset spin state on chat switch
  useEffect(() => {
    if (chatKey !== prevChatKeyRef.current) {
      prevChatKeyRef.current = chatKey
      hasStoppedRotation.current = !shouldSpin
    }
  }, [chatKey, shouldSpin])

  // Marker-driven stop/restart
  useEffect(() => {
    if (!shouldSpin) {
      if (!hasStoppedRotation.current) {
        stopRotation()
      }
    } else if (isMapLoaded && !aiCameraActive) {
      hasStoppedRotation.current = false
      const map = mapRef.current?.getMap()
      if (map) {
        map.jumpTo({
          center: [DEFAULT_VIEW_STATE.longitude, DEFAULT_VIEW_STATE.latitude],
          zoom: DEFAULT_VIEW_STATE.zoom,
        })
      }
      setRotationKey(k => k + 1)
    }
  }, [shouldSpin, isMapLoaded, stopRotation, mapRef, aiCameraActive])

  // Spin lifecycle
  useEffect(() => {
    if (!isMapLoaded || hasStoppedRotation.current || shouldSkipAnimation) {
      if (shouldSkipAnimation && !hasStoppedRotation.current) {
        hasStoppedRotation.current = true
      }
      return
    }

    const map = mapRef.current?.getMap()
    if (!map) return

    map.on('moveend', spinGlobe)
    spinGlobe()

    return () => {
      map.off('moveend', spinGlobe)
    }
  }, [isMapLoaded, spinGlobe, rotationKey, shouldSkipAnimation, mapRef])

  return { stopRotation }
}
